import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ReactComponent as SearchIcon } from './search-icon.svg';
import { CheckBox } from '../CheckBox/CheckBox';
import Input from '../Input/Input';
import { classNames } from '../utils';
import './InputDropDown.scss';

export const InputDropDownSearch = ({
  autoComplete = 'off',
  options = [],
  specificOptions = [],
  name,
  error,
  value,
  onChange,
  label = '',
  placeholder,
  subText,
  className,
  topLabel,
  initialStatus,
  isRequired,
  isMulti,
  isDisabled,
  isClearSearchValue = false,
  setIsClearSearchValue,
  isClearValueAfterClickOutside = false,
  usePortal = true
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const menuId = useId();

  const inputClasses = classNames({
    'input-wrapper-dropdown': true,
    active: isMenuOpen || value?.length,
    disabled: isDisabled,
    'multi-select': isMulti,
    error: !!error,
    hit: !error && !!initialStatus,
    [className]: true
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnMenu = event?.target?.closest && event?.target?.closest('.input-dropdown-menu');
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target) && !isClickOnMenu) {
        setIsMenuOpen(false);

        if (!value) {
          setSearchText('');
        } else {
          if (!searchText && isClearValueAfterClickOutside) {
            onChange(name, '');
          } else {
            setSearchText(getValue() || '');
          }
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownRef, value, searchText]);

  useEffect(() => {
    if (value && value !== searchText) {
      setSearchText(getValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  useEffect(() => {
    if (isClearSearchValue) {
      setSearchText('');
      setIsClearSearchValue(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClearSearchValue]);

  useEffect(() => {
    if (!isMenuOpen || !usePortal) return;
    const updateMenuPosition = () => {
      if (!dropdownRef?.current) return;
      const rect = dropdownRef?.current?.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.top + rect.height + 2,
        left: rect.left,
        width: rect.width,
        zIndex: 9999
      });
    };
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isMenuOpen, usePortal]);

  const getValue = () => {
    if (isMulti) {
      const multiValue = (options || [])?.filter((option) => value?.includes(option?.key));
      return multiValue.map((val) => val?.value)?.join('/');
    }
    return options?.find((option) => option?.key === value)?.value;
  };

  const inputChange = (data) => {
    if (isMulti) {
      const valueSet = value?.includes(data) ? value?.filter((val) => val !== data) : [...value, data];

      return onChange(name, valueSet);
    }
    onChange(name, data);
    setIsMenuOpen(false);
  };

  const onSearchFocus = (e) => {
    if (options && !isDisabled && !(isMenuOpen && e?.target?.name === name)) {
      setIsMenuOpen(!isMenuOpen);
    }
  };

  const onSearchChange = (e) => {
    setSearchText(e?.target?.value);
  };

  const menuContent = (
    <div
      className={'input-dropdown-menu'}
      role={'listbox'}
      id={menuId}
      tabIndex={-1}
      aria-label={label || name}
      style={usePortal ? menuStyle : undefined}
      onMouseDown={(e) => e?.preventDefault()}
    >
      {options &&
        options
          ?.filter((dataPoint) => dataPoint?.value?.toLowerCase()?.startsWith(searchText?.toLowerCase()))
          ?.map((dataPoint) => (
            <CheckBox
              key={dataPoint?.key}
              onChange={() => inputChange(dataPoint?.key)}
              isActive={(isMulti && value?.includes(dataPoint?.key)) || dataPoint?.key === value}
              showCheckBox={isMulti}
              value={dataPoint?.value}
            />
          ))}
      {specificOptions &&
        specificOptions?.map((dataPoint) => (
          <CheckBox
            key={dataPoint?.key}
            onChange={() => inputChange(dataPoint?.key)}
            isActive={(isMulti && value?.includes(dataPoint?.key)) || dataPoint?.key === value}
            showCheckBox={isMulti}
            value={null}
            content={dataPoint?.content}
          />
        ))}
    </div>
  );

  return (
    <div
      ref={dropdownRef}
      role={'combobox'}
      aria-haspopup={'listbox'}
      aria-expanded={isMenuOpen}
      aria-controls={menuId}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e?.key === 'Enter') {
          e?.preventDefault();
          onSearchFocus(e);
        }
      }}
      onClick={onSearchFocus}
      className={inputClasses}
    >
      {topLabel && <p className={'top-label'}>{topLabel}</p>}
      <Input
        label={label + (isRequired ? '*' : '')}
        autoComplete={autoComplete}
        name={name}
        value={searchText}
        onChange={(e) => onSearchChange(e)}
        Icon={SearchIcon}
        className={'input-dropdown-input'}
        placeholder={placeholder}
        isDisabled={isDisabled}
      />
      {isMenuOpen && (usePortal ? createPortal(menuContent, document?.body) : menuContent)}
      {(error || subText) && <p className={`input-sub-text ${error ? 'input-error' : ''}`}>{error || subText}</p>}
    </div>
  );
};

InputDropDownSearch.propTypes = {
  autoComplete: PropTypes.string,
  options: PropTypes.array,
  specificOptions: PropTypes.array,
  name: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  subText: PropTypes.string,
  className: PropTypes.string,
  topLabel: PropTypes.string,
  initialStatus: PropTypes.number,
  isRequired: PropTypes.bool,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isClearSearchValue: PropTypes.bool,
  setIsClearSearchValue: PropTypes.func,
  isClearValueAfterClickOutside: PropTypes.bool,
  usePortal: PropTypes.bool
};
